import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Badge } from '@twilio-paste/core/badge';
import { Box } from '@twilio-paste/core/box';
import { CheckboxCheckIcon } from '@twilio-paste/icons/esm/CheckboxCheckIcon';
import {
  MediaObject,
  MediaFigure,
  MediaBody,
} from '@twilio-paste/core/media-object';
import {
  Menu,
  MenuBadge,
  MenuGroup,
  MenuItem,
  MenuSeparator,
  useMenuState,
} from '@twilio-paste/core/menu';
import { Spinner } from '@twilio-paste/core/spinner';
import { Truncate } from '@twilio-paste/core/truncate';
import {
  AccountDetails,
  AccountStatus,
  dataTestIds,
  oscLayoutCopy,
  routes,
} from '@twiliointernal/e2-commons-shared-definitions';
import { useSegment } from '@twiliointernal/e2-commons-shared-hooks';
import { logger } from '@twiliointernal/e2-commons-shared-libs';

import { constants } from '../definitions';
import {
  usePreviousState,
  useToasterContext,
  useUserDetails,
} from '../../../hooks';
import { trackingDetails } from '../../../definitions/constants';
import { UpdateCurrentAccountSidOptions } from '../../../providers/userProvider/definitions/types';

import styles from '../layout.module.css';

const AccountSwitcherMenu = (): ReactElement => {
  const menu = useMenuState();
  const navigate = useNavigate();
  const toaster = useToasterContext();

  const {
    account,
    accounts,
    hasAccountSwitcherError,
    isLoading,
    isLoadingAccountSwitcher,
    isLoggedIn,
    isOnSubaccount,
    updateCurrentAccountSid,
    user,
  } = useUserDetails();
  const { trackEvent } = useSegment({
    accountSid: account?.sid,
    userSid: user?.sid,
    isLoading,
    isLoggedIn,
  });
  /**
   * !IMPORTANT: The `user.currentAccountSid` reflects the main accountSid,
   * but not a subaccount if selected. However, the `account.sid` WILL reflect
   * a subaccount, not the main accountSid.
   */
  const [currentMainAccount, updateCurrentMainAccount] = useState<
    AccountDetails | undefined
  >(undefined);
  const previousAccountName = usePreviousState(account?.friendlyName);
  const currentAccountName = account?.friendlyName;

  useEffect(() => {
    if (hasAccountSwitcherError) {
      logger.error(`${constants.logPrefixes.accountSwitcherMenu}`);
      toaster.push({
        message: oscLayoutCopy.topbar.accountSwitcher.error,
        variant: 'error',
        dismissAfter: 5000,
      });
    }
  }, [hasAccountSwitcherError]);

  useEffect(() => {
    if (user && account) {
      logger.info(
        `${constants.logPrefixes.accountSwitcherMenu}::Initial sid on page load`,
        {
          user: user?.sid,
          currentAccountSid: account.sid,
        },
      );
    }
  }, []);

  useEffect(() => {
    if (accounts?.length && account) {
      const currentAccount = accounts?.find((singleAccount) => {
        // If on a subaccount, use the `parentAccountSid` for the main account
        const accountSidToUse = account?.parentAccountSid || account?.sid;
        return singleAccount.sid === accountSidToUse;
      });

      updateCurrentMainAccount(currentAccount);
    }
  }, [account]);

  useEffect(() => {
    if (previousAccountName && previousAccountName !== currentAccountName) {
      if (currentAccountName) {
        toaster.push({
          message:
            oscLayoutCopy.topbar.accountSwitcher.switchedAccountNotification(
              currentAccountName,
            ),
          variant: 'neutral',
          dismissAfter: 5000,
        });
      }
    }
  }, [previousAccountName, currentAccountName]);

  /**
   * TODO: Fix logic to assume `accounts` is always defined as user's will
   *  always have at least one account. Can be fixed by TODO in userProvider types
   */
  if (!accounts) {
    return <div />;
  }

  // Only list the first 5 accounts
  const userAccountsToDisplay = accounts.slice(0, 5);

  const handleTrackingEvent = (sid: string): void => {
    const eventName = trackingDetails.accountSwitcherLinkInMenuClicked;
    const trackingContext = {
      user: user?.sid,
      currentAccountSid: account?.sid,
      requestedAccountSid: sid,
    };
    logger.info(
      `${constants.logPrefixes.accountSwitcherMenu}::[${eventName}]`,
      trackingContext,
    );
    trackEvent(eventName);
  };

  const handleAccountSwitch = async (
    selectedAccount: AccountDetails,
    options: UpdateCurrentAccountSidOptions = {},
  ): Promise<void> => {
    const isSuccessful = await updateCurrentAccountSid({
      newSid: selectedAccount.sid,
      options,
    });

    if (isSuccessful) {
      updateCurrentMainAccount(selectedAccount);

      handleTrackingEvent(selectedAccount.sid);

      navigate(routes.home);
    }

    menu.hide();
  };

  const handleNavigate = (route: string): void => {
    navigate(route);
    menu.hide();
  };

  const renderAccountMenuItems = (): ReactElement => (
    <MenuGroup
      {...menu}
      label={oscLayoutCopy.topbar.accountSwitcher.menuGroupLabel}
    >
      {userAccountsToDisplay
        .filter((accout) => accout.status !== AccountStatus.deleted)
        .map((account) => (
          <MenuItem
            {...menu}
            key={account.sid}
            onClick={(): void => {
              handleAccountSwitch(account);
            }}
          >
            <Box maxWidth="size20">
              {currentMainAccount?.sid === account.sid ? (
                <MediaObject verticalAlign="center">
                  <Truncate title={account.friendlyName}>
                    <MediaBody>{account.friendlyName}</MediaBody>
                  </Truncate>
                  <MediaFigure spacing="space60" align="end">
                    <CheckboxCheckIcon decorative={true} />
                  </MediaFigure>
                </MediaObject>
              ) : (
                <Truncate title={account.friendlyName}>
                  {account.friendlyName}
                </Truncate>
              )}
            </Box>
          </MenuItem>
        ))}
    </MenuGroup>
  );

  /**
   * Note: Even though this action is switching from a subaccount to an
   * account, we need to use the `switchSubAccount` endpoint, not the
   * `switchAccount` endpoint, which is why { isSubaccount: true } is
   * passed to `handleAccountSwitch`.
   *
   * Using a non-null assertion because subaccount menu will only render if
   * a currentMainAccount exists. This can be fixed with the TODO in the
   * userProvider types
   */
  const renderSubAccountMenuItems = (): ReactElement => (
    <MenuItem
      {...menu}
      key={account?.sid}
      onClick={(): void => {
        handleAccountSwitch(currentMainAccount!, { isSubaccount: true });
      }}
    >
      {oscLayoutCopy.topbar.accountSwitcher.switchToMainAccountButtonLabel}
    </MenuItem>
  );

  const isCurrentAccountNameLoaded = !currentMainAccount?.friendlyName;

  if (isCurrentAccountNameLoaded || isLoadingAccountSwitcher) {
    return (
      <Box as="div" className={styles.spinnerContainer}>
        <Spinner decorative={false} title="Loading" size="sizeIcon10" />
      </Box>
    );
  }

  /**
   * The "account" in state will reflect either an account OR subaccount.
   * Here we use it as a subaccount name since `accounts` from `userProvider`
   * is the main source of truth in this component.
   */
  const subaccountName = account?.friendlyName;

  return (
    <Box as="span" className={styles.topbarActionContainer}>
      <MenuBadge
        {...menu}
        i18nButtonLabel={oscLayoutCopy.topbar.accountSwitcher.buttonLabel}
        variant="default"
        data-testid={dataTestIds.accountSwitcher.accountSwitcherMenu.menuBadge}
      >
        {currentMainAccount && (
          <Box
            as="span"
            maxWidth="size20"
            data-testid={
              dataTestIds.accountSwitcher.accountSwitcherMenu.menuCurrentAccount
            }
          >
            <Truncate title={currentMainAccount.friendlyName}>
              {currentMainAccount.friendlyName}
            </Truncate>
          </Box>
        )}
      </MenuBadge>
      <Menu
        {...menu}
        aria-label={oscLayoutCopy.topbar.accountSwitcher.ariaLabel}
        data-testid={dataTestIds.accountSwitcher.accountSwitcherMenu.dropdown}
      >
        {isOnSubaccount
          ? renderSubAccountMenuItems()
          : renderAccountMenuItems()}
        <MenuSeparator />

        <MenuItem
          {...menu}
          onClick={(): void => {
            handleNavigate(routes.accountSwitcher);
          }}
        >
          {oscLayoutCopy.topbar.accountSwitcher.viewAllAccounts}
        </MenuItem>

        <MenuSeparator />

        <MenuItem
          {...menu}
          onClick={(): void => {
            handleNavigate(routes.subaccounts);
          }}
        >
          {oscLayoutCopy.topbar.accountSwitcher.viewAllSubaccounts}
        </MenuItem>
      </Menu>

      {isOnSubaccount && (
        <Badge as="span" variant="subaccount">
          {/* TODO: Below empty string as fallback won't be necessary with update to userProvider types */}
          {subaccountName || ''}
        </Badge>
      )}
    </Box>
  );
};

export default AccountSwitcherMenu;
